import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchResultsTable } from "@/components/search/SearchResultsTable";
import type { SortOption } from "@/components/search/ActiveSearchTabs";
import type { GeoCoords, DatabaseLoad } from "@/lib/loadSearch";

interface SimilarResultsSectionProps {
  similarLoads: DatabaseLoad[];
  count: number;
  isLoading: boolean;
  searchOriginCoords?: GeoCoords | null;
  searchDestCoords?: GeoCoords | null;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
  meta?: {
    sources: Array<'layer1' | 'layer2' | 'layer3'>;
    originState: string | null;
    destState: string | null;
  } | null;
}

function buildSubtitle(meta: SimilarResultsSectionProps['meta']): string {
  if (!meta || meta.sources.length === 0) return '(outside search radius)';
  const s = new Set(meta.sources);
  const hasL1 = s.has('layer1');
  const hasL2 = s.has('layer2');
  const hasL3 = s.has('layer3');
  const lane = meta.originState && meta.destState
    ? `${meta.originState} → ${meta.destState}`
    : null;

  if (hasL1 && !hasL2 && !hasL3) return '(outside search radius)';
  if (hasL1 && hasL2 && !hasL3) return '(including state-to-state matches)';
  if (!hasL1 && hasL2 && !hasL3 && lane) return `Loads across ${lane}`;
  if (hasL3 && (hasL2 || hasL1) && lane) return `Loads across ${lane} (with any equipment)`;
  if (!hasL1 && !hasL2 && hasL3 && lane) return `Loads across ${lane} (any equipment)`;
  return '(outside search radius)';
}

export function SimilarResultsSection({
  similarLoads,
  count,
  isLoading,
  searchOriginCoords,
  searchDestCoords,
  sortBy,
  onSortChange,
  meta,
}: SimilarResultsSectionProps) {
  const [open, setOpen] = useState(false);

  if (!isLoading && count === 0) return null;

  const subtitle = buildSubtitle(meta);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2">
      <CollapsibleTrigger asChild>
        <button
          className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-white cursor-pointer transition-colors border-t border-border"
          style={{ backgroundColor: open ? '#026B65' : '#02857E' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#026B65'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = open ? '#026B65' : '#02857E'; }}
        >
          <ChevronRight className={cn("h-4 w-4 transition-transform", open && "rotate-90")} />
          {isLoading && !count
            ? "Loading similar results…"
            : `+${count} Similar Results`
          }
          <span className="text-[10px] font-normal text-white/80 ml-1">
            {subtitle}
          </span>
          <span className="ml-auto text-[11px] font-medium text-white/90 uppercase tracking-wide">
            {open ? 'Click to collapse' : 'Click to expand ↓'}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {isLoading ? (
          <div className="px-4 py-4 space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : (
          <SearchResultsTable
            loads={similarLoads}
            searchOriginCoords={searchOriginCoords}
            searchDestCoords={searchDestCoords}
            sortBy={sortBy}
            onSortChange={onSortChange}
          />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
